import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Presentation, Loader2, Edit, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft:    { bg: '#f3f4f6', color: '#6b7280' },
  sent:     { bg: '#dbeafe', color: '#1e40af' },
  accepted: { bg: '#d1fae5', color: '#065f46' },
  declined: { bg: '#fee2e2', color: '#991b1b' },
};

function ProposalStatusBadge({ status }) {
  const key = status || 'draft';
  const s = STATUS_STYLES[key] || STATUS_STYLES.draft;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color }}
    >
      {key}
    </span>
  );
}

/** Proposals belonging to a single project. */
export default function ProjectProposalsTab({ project }) {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/proposals`, { params: { project_id: project.id } })
      .then(res => { if (!cancelled) setProposals(res.data?.data || []); })
      .catch(err => console.error('Failed to fetch project proposals:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id]);

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="text-center py-16">
        <Presentation className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
        <p className="text-[#6b7280] text-lg mb-2">No proposals for this project</p>
        <p className="text-[#6b7280]/60 text-sm mb-6">Proposals created here will be linked to {project.project_name}</p>
        <Button
          onClick={() => navigate(`/proposal/new?from_project=${project.id}`)}
          className="bg-[#1a1a2e] text-white hover:bg-[#252545]"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Proposal
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Proposals ({proposals.length})
        </h3>
        <Button
          onClick={() => navigate(`/proposal/new?from_project=${project.id}`)}
          size="sm"
          className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Proposal
        </Button>
      </div>

      <div className="space-y-1">
        <div
          className="hidden sm:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
          style={{ gridTemplateColumns: '150px 1fr 110px 100px 60px' }}
        >
          <span>Proposal No</span>
          <span>Client</span>
          <span>Date</span>
          <span>Status</span>
          <span className="text-right">Open</span>
        </div>

        {proposals.map(p => (
          <div
            key={p.id}
            className="grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
            style={{
              gridTemplateColumns: '150px 1fr 110px 100px 60px',
              borderBottom: '1px solid rgba(201,168,76,0.1)',
            }}
            onClick={() => navigate(`/proposal/${p.id}?from_project=${project.id}`)}
          >
            <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{p.proposal_no || '—'}</span>
            <span className="text-sm truncate">{p.variables?.client_name || '—'}</span>
            <span className="text-sm text-[#6b7280]">{p.variables?.proposal_date || '—'}</span>
            <span><ProposalStatusBadge status={p.status} /></span>
            <div className="flex items-center justify-end">
              <Edit className="w-4 h-4 text-[#6b7280]" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
